import { Theme } from "@mui/material";
import { createStyles, WithStyles, withStyles } from "@mui/styles";
import { action, makeObservable, observable } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
import { DialogChildProps, Layout, LayoutChildProps, LayoutConfig } from "../config";
import AppServer, { State as AppServerState } from "../core/AppServer";
import Localization from "../core/Localization";
import Sys from "../core/Sys";
import Button from "../coreui/Button";
import Dialog, { BreakPointColumn } from "../coreui/Dialog";
import DialogActions from "../coreui/DialogActions";
import DialogContent from "../coreui/DialogContent";
import Presentation from "../coreui/Presentation";
import ProcessingMask from "../coreui/ProcessingMask";
import Typography from "../coreui/Typography";
import PaneRow from "../models/PaneRow";
import RoundTripService from "../services/RoundTripService";
import SelectControlService, {
  GetSelectDialogConfigResponse,
  OnDialogCloseResponse,
  OnDialogOpenResponse,
  OnSearchResponse,
} from "../services/SelectControlService";
import ErrorsStore from "../stores/ErrorsStore";
import PaneDataStore from "../stores/PaneDataStore";
import RequestsStore from "../stores/RequestsStore";
import { AccessLevel } from "./AccessLevel";
import { EmbeddedAddOn as EmbeddedAddOnBase } from "./EmbeddedAddOn";
import Grid from "./Grid";
import GridItem from "./GridItem";
import Panel from "./Panel";

interface ConfigProperties {
  criteriaPane: object;
  dataId: string;
  dialogResultsDescription: string;
  dialogTitle: string;
  disabledHelpText: string;
  isSingleSelect: boolean;
  name: string;
  propagated?: DialogChildProps;
  selectDialogId: number;
  selectedDataId: string;
  selectedGrid: object;
  showInlineCriteria: boolean;
}

export interface SelectChildProps extends LayoutChildProps {
  dialogResultsDescription: string;
  parentRowObjectHandle?: string;
  parentSelect: {
    configProps: {
      dataId: string;
      disabledHelpText: string;
      isSingleSelect: boolean;
      name: string;
    };
    getSelectedRowObjectHandles: () => string[];
    isDialogClosing: boolean;
    isDialogOpen: boolean;
    onIsLoadingChanged: (isLoading: boolean) => void;
    onRowsChanged: (selected: boolean, rowKeys: string[]) => void;
    onRowSelected: (rowKey: string) => void;
    onRowsRemoved: (rowKeys: string[]) => void;
    onRowUnselected: (rowKey: string) => void;
    search: (criteriaWidgetName: string | null) => void;
    selectedDataId: string;
  };
}

export interface RuntimeProperties {
  accessLevel: AccessLevel;
  businessErrors: string[];
  canAddRemoveRows: boolean;
  showDisabledHelp: boolean;
}

interface State {
  breakPointColumns?: BreakPointColumn[];
  isDialogClosing?: boolean;
  isDialogLoading?: boolean;
  isDialogOpen?: boolean;
  isDialogSearching?: boolean;
}

const styles = (theme: Theme) => {
  const dialogWidths = {};
  for (const breakPoint of theme.spacingBreakPoints.filter((b) => b !== "xs")) {
    const maxWidth = theme.panel.maxWidths[breakPoint];

    dialogWidths[`dialog-${breakPoint}`] = {
      [theme.breakpoints.up(breakPoint)]: {
        width: maxWidth,
      },
    };
  }

  return createStyles({
    ...dialogWidths,
    errorText: {
      color: theme.palette.danger.main,
    },
  });
};

export class SelectControl extends React.Component<
  ConfigProperties & WithStyles<typeof styles>,
  State
> {
  private dialogLayoutConfig: LayoutConfig | null = null;
  private childSelector: string = "button, input";
  private componentId: string = `select-control-${Sys.nextId}`;
  private criteriaRef: React.RefObject<HTMLDivElement>;
  private dialogId: string = `${this.componentId}-dialog`;
  private dialogLabelId: string = `${this.dialogId}-labelled-by`;
  private propagated: SelectChildProps;
  private lastElementFocused: HTMLElement | null = null;
  private recoveryState: AppServerState | null = null;
  private readonly selectedDataId: string;
  private selectionChanges: {
    addedRowKeys: string[];
    removedRowKeys: string[];
  };

  public constructor(props: ConfigProperties & WithStyles<typeof styles>) {
    super(props);

    makeObservable<
      SelectControl,
      "dialogLayoutConfig" | "setDialogLayoutConfig"
    >(this, {
      dialogLayoutConfig: observable,
      setDialogLayoutConfig: action,
    });

    this.state = {
      isDialogClosing: false,
      isDialogLoading: false,
      isDialogOpen: false,
      isDialogSearching: false,
    };

    this.selectedDataId = props.selectedDataId;
    this.selectionChanges = { addedRowKeys: [], removedRowKeys: [] };

    this.propagated = {
      dialogResultsDescription: props.dialogResultsDescription,
      parentSelect: {
        configProps: {
          dataId: props.dataId,
          disabledHelpText: props.disabledHelpText,
          isSingleSelect: props.isSingleSelect,
          name: props.name,
        },
        getSelectedRowObjectHandles: this.getCurrentlySelectedRowObjectHandles,
        isDialogClosing: false,
        isDialogOpen: false,
        onIsLoadingChanged: (_isLoading: boolean) => {},
        onRowsChanged: this.onRowsChanged,
        onRowSelected: this.onRowSelected,
        onRowsRemoved: this.onRowsRemoved,
        onRowUnselected: this.onRowUnselected,
        search: this.search,
        selectedDataId: props.selectedDataId,
      },
    };

    this.criteriaRef = React.createRef<HTMLDivElement>();
  }

  private acceptDialog = (): void => {
    if (
      this.selectionChanges.addedRowKeys.length <= 0 &&
      this.selectionChanges.removedRowKeys.length <= 0
    ) {
      this.cancelDialog();

      return;
    }

    if (this.state.isDialogClosing) {
      return;
    }

    this.setState({
      isDialogClosing: true,
      isDialogLoading: true,
    });
    ErrorsStore.clearBusinessErrorsForWidget(
      this.props.dataId,
      this.props.name
    );

    const row: PaneRow = PaneRow.get(this.props.dataId)!;
    SelectControlService.onDialogClose(
      row.rowKey,
      this.props.dataId,
      this.props.name,
      this.props.propagated?.parentDialog?.rowKey ?? null,
      this.selectionChanges.addedRowKeys,
      this.selectionChanges.removedRowKeys
    )
      .then((response: OnDialogCloseResponse) => {
        ErrorsStore.clearErrors();

        if (response.validationErrors.length > 0) {
          ErrorsStore.clearErrors();
          ErrorsStore.showErrors(response.validationErrors);

          this.setState({ isDialogClosing: false });

          return;
        }

        if (response.businessErrors.length > 0) {
          ErrorsStore.clearErrors();
          ErrorsStore.setBusinessErrors(response.businessErrors, false);

          this.setState({ isDialogClosing: false });

          return;
        }

        this.recoveryState = null;
        AppServer.setState(response.appServerState);

        // FUTURE
        // This loadResponse() call causes the data on the results grid in the
        // dialog to be populated again. This is neither efficient nor rational
        // and should be fixed.
        PaneDataStore.loadResponse(response.paneDataByDataId);
        EmbeddedAddOnBase.resolveRoundTrip();
        this.setState({ isDialogOpen: false });
      })
      .finally(() => {
        this.setState({ isDialogLoading: false });
      });
  };

  private cancelDialog = (): void => {
    if (this.state.isDialogClosing) {
      return;
    }
    this.onDialogClose();
  };

  private getCurrentlySelectedRowObjectHandles = (): string[] => {
    let selectedObjectHandles: string[] =
      this.getInitiallySelectedRowObjectHandles();

    selectedObjectHandles = selectedObjectHandles.filter(
      (h) => !this.selectionChanges.removedRowKeys.includes(h)
    );

    selectedObjectHandles = selectedObjectHandles.concat(
      this.selectionChanges.addedRowKeys
    );

    return selectedObjectHandles;
  };

  private getInitiallySelectedRowObjectHandles = (): string[] => {
    const rows: PaneRow[] = PaneDataStore.getPaneCollection(
      this.selectedDataId
    );

    const selectedObjectHandles: string[] = [];
    for (const row of rows) {
      selectedObjectHandles.push(row.objectHandle);
    }

    return selectedObjectHandles;
  };

  private onDialogClose = () => {
    ErrorsStore.clearErrors();
    this.setState({
      isDialogClosing: true,
      isDialogOpen: false,
    });

    AppServer.setState(this.recoveryState);
    this.recoveryState = null;

    EmbeddedAddOnBase.rejectRoundTrip();
  };

  private onDialogExited = (): void => {
    this.setDialogLayoutConfig(null);

    // Manually focus the element since the processing animation interferes
    // with the dialog returning the focus
    this.lastElementFocused!.focus();
  };

  private onDialogEntered = (node: HTMLElement, isAppearing: boolean) => {
    const firstFocusable: HTMLElement | null = node.querySelector(
      this.childSelector
    );
    if (firstFocusable) {
      firstFocusable.focus();
    }
  };

  private onRowsChanged = (added: boolean, rowKeys: string[]): void => {
    const initialRowKeys = this.getInitiallySelectedRowObjectHandles();
    const { addedRowKeys, removedRowKeys } = this.selectionChanges;

    for (const rowKey of rowKeys) {
      if (added) {
        if (
          !initialRowKeys.includes(rowKey) &&
          !addedRowKeys.includes(rowKey)
        ) {
          addedRowKeys.push(rowKey);
        }
        const index = removedRowKeys.indexOf(rowKey);
        if (index >= 0) {
          removedRowKeys.splice(index, 1);
        }
      } else {
        if (
          initialRowKeys.includes(rowKey) &&
          !removedRowKeys.includes(rowKey)
        ) {
          removedRowKeys.push(rowKey);
        }
        const index = addedRowKeys.indexOf(rowKey);
        if (index >= 0) {
          addedRowKeys.splice(index, 1);
        }
      }
    }
  };

  private onRowSelected = (rowKey: string): void => {
    const index = this.selectionChanges.removedRowKeys.indexOf(rowKey);
    if (index >= 0) {
      this.selectionChanges.removedRowKeys.splice(index, 1);
    } else if (this.selectionChanges.addedRowKeys.indexOf(rowKey) < 0) {
      if (this.props.isSingleSelect) {
        this.selectionChanges.addedRowKeys = [rowKey];

        const selectedRowKeys: string[] =
          this.getInitiallySelectedRowObjectHandles();
        if (
          selectedRowKeys.length > 0 &&
          !this.selectionChanges.removedRowKeys.includes(selectedRowKeys[0])
        ) {
          this.selectionChanges.removedRowKeys.push(selectedRowKeys[0]);
        }
      } else {
        this.selectionChanges.addedRowKeys.push(rowKey);
      }
    }
  };

  private onRowsRemoved = (rowKeys: string[]): void => {
    RoundTripService.standardRoundTrip(
      "SelectControl/OnRemoveRows",
      this.props,
      { removedObjectHandles: rowKeys }
    ).catch((reason) => {
      if (reason) {
        throw reason;
      }
    });

    if (
      this.props.criteriaPane &&
      this.criteriaRef.current &&
      this.getCurrentlySelectedRowObjectHandles().length === rowKeys.length
    ) {
      const element: HTMLElement | null =
        this.criteriaRef.current.querySelector(this.childSelector);

      element?.focus();
    }
  };

  private onRowUnselected = (rowKey: string): void => {
    const index = this.selectionChanges.addedRowKeys.indexOf(rowKey);
    if (index >= 0) {
      this.selectionChanges.addedRowKeys.splice(index, 1);
    } else if (this.selectionChanges.removedRowKeys.indexOf(rowKey) < 0) {
      this.selectionChanges.removedRowKeys.push(rowKey);
    }
  };


  private openDialog(criteriaWidgetName: string | null): void {
    this.lastElementFocused = document.activeElement as HTMLElement;
    RequestsStore.instance.processingStarted();
    EmbeddedAddOnBase.roundTripStarting();
    ErrorsStore.clearErrors();

    const row: PaneRow = PaneRow.get(this.props.dataId)!;
    const dialogOpenRequest = SelectControlService.onDialogOpen(
      row.rowKey,
      this.props.dataId,
      this.props.name,
      criteriaWidgetName
    );

    const configRequest = SelectControlService.getSelectDialogConfig(
      this.props.selectDialogId
    );

    this.selectionChanges.addedRowKeys = [];
    this.selectionChanges.removedRowKeys = [];

    Promise.all([dialogOpenRequest, configRequest])
      .then(
        ([dialogOpenResponse, configResponse]: [
          OnDialogOpenResponse,
          GetSelectDialogConfigResponse,
        ]) => {
          if (dialogOpenResponse.validationErrors.length > 0) {
            ErrorsStore.clearErrors();
            ErrorsStore.showErrors(dialogOpenResponse.validationErrors);
            EmbeddedAddOnBase.rejectRoundTrip();

            return;
          }

          if (dialogOpenResponse.dataChangesBusinessErrors.length > 0) {
            ErrorsStore.clearBusinessErrors();
            ErrorsStore.setBusinessErrors(
              dialogOpenResponse.dataChangesBusinessErrors,
              false
            );
            ErrorsStore.pushErrorsToWidgets();
            EmbeddedAddOnBase.rejectRoundTrip();

            return;
          }

          if (dialogOpenResponse.criteriaBusinessErrors.length > 0) {
            ErrorsStore.setBusinessErrors(
              dialogOpenResponse.criteriaBusinessErrors,
              true
            );
          }

          this.recoveryState = AppServer.getState();
          AppServer.setState(dialogOpenResponse.appServerState);
          PaneDataStore.loadResponse(dialogOpenResponse.paneDataByDataId);

          this.setDialogLayoutConfig(configResponse.dialogLayout);

          this.setState({
            breakPointColumns: configResponse.breakPoints,
            isDialogClosing: false,
            isDialogOpen: true,
          });
        }
      )
      .finally(() => RequestsStore.instance.processingStopped());
  }

  private search = (criteriaWidgetName: string | null): void => {
    if (!this.state.isDialogOpen) {
      this.openDialog(criteriaWidgetName);

      return;
    }

    this.setState({ isDialogSearching: true });
    const row: PaneRow = PaneRow.get(this.props.dataId)!;
    SelectControlService.onSearch(
      row.rowKey,
      this.props.dataId,
      this.props.name,
      criteriaWidgetName
    )
      .then((response: OnSearchResponse) => {
        if (response.businessErrors.length > 0) {
          ErrorsStore.setBusinessErrors(response.businessErrors, true);
        }

        AppServer.setState(response.appServerState);
        PaneDataStore.loadResponse(response.paneDataByDataId);
      })
      .finally(() => {
        this.setState({
          isDialogLoading: false,
          isDialogSearching: false,
        });
      });
  };

  private setDialogLayoutConfig(value: LayoutConfig | null) {
    this.dialogLayoutConfig = value;
  }

  public render(): React.ReactNode {
    const row = PaneRow.get(this.props.dataId);
    if (!row) {
      return null;
    }

    const widget = row.getWidgetT<null, RuntimeProperties>(this.props.name);

    if (widget.properties.accessLevel === AccessLevel.hidden) {
      return null;
    }

    let criteriaPane = null;
    if (this.props.criteriaPane) {
      criteriaPane = Presentation.create(
        this.props.criteriaPane,
        this.propagated
      );
    }

    const selectedGrid = Presentation.create(
      this.props.selectedGrid,
      this.propagated
    );

    if (widget.properties.accessLevel === AccessLevel.disabled) {
      return (
        <div>
          {criteriaPane}
          {selectedGrid}
        </div>
      );
    }

    this.propagated.parentSelect.isDialogClosing = !!this.state.isDialogClosing;
    this.propagated.parentSelect.isDialogOpen = !!this.state.isDialogOpen;

    // The wrapping divs are used to prevent the Grid margin from overriding
    // the parent GridItem margin
    return (
      <div>
        <Grid grouping="Closely Related" lg={1} md={1} sm={1} xs={1}>
          {criteriaPane !== null ? (
            <GridItem lg={1} md={1} sm={1} xl={1} xs={1}>
              <div ref={this.criteriaRef} style={{ position: "relative" }}>
                {criteriaPane}
              </div>
            </GridItem>
          ) : null}
          <GridItem
            md={1}
            lg={1}
            sm={1}
            xl={1}
            xs={1}
          >
            {selectedGrid}
          </GridItem>
        </Grid>
        <Dialog
          aria-labelledby={this.dialogLabelId}
          breakPointColumns={this.state.breakPointColumns}
          onClose={this.onDialogClose}
          open={this.state.isDialogOpen!}
          TransitionProps={{
            onEntered: this.onDialogEntered,
            onExited: this.onDialogExited,
          }}
        >
          <ProcessingMask
            isOpen={
              this.state.isDialogLoading! || this.state.isDialogSearching!
            }
            openDelayMilliseconds={this.state.isDialogSearching ? 350 : 0}
            trapFocus={true}
          />
          <div id={this.dialogLabelId} className="screenReaderOnly">
            {this.props.dialogTitle}
          </div>
          <DialogContent>
            <Grid grouping="Closely Related" lg={1} md={1} sm={1} xs={1}>
              <GridItem lg={1} md={1} sm={1} xl={1} xs={1}>
                <Typography variant="h3">{this.props.dialogTitle}</Typography>
              </GridItem>
              <GridItem lg={1} md={1} sm={1} xl={1} xs={1}>
                {this.dialogLayoutConfig !== null ? (
                  <Panel presentationId={this.dialogLayoutConfig.layoutId}>
                    <Layout
                      config={this.dialogLayoutConfig}
                      propagated={this.propagated}
                    />
                  </Panel>
                ) : null}
              </GridItem>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.acceptDialog}>
              {Localization.getBuiltInMessage("select")}
            </Button>
            <Button onClick={this.cancelDialog} style={{ marginLeft: 40 }}>
              {Localization.getBuiltInMessage("cancel")}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}

export default withStyles(styles)(observer(SelectControl));
